import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Button,
  Stack,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Popper,
  Paper,
  ClickAwayListener,
  Grid,
  Typography,
  Divider,
  Collapse,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BoltIcon from '@mui/icons-material/Bolt';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_GRADIENTS } from '../../../config/brand';
import { usePublicRegistrationFlow } from '../../../contexts/PublicRegistrationFlowContext';

// Stil tanımlamaları
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: BRAND_GRADIENTS.primary,
  boxShadow: 'none',
  padding: theme.spacing(1, 2),
  zIndex: 1000,
  borderRadius: 0,
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: 'white',
  fontWeight: 500,
  fontSize: '1.15rem',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  width: 280,
  paddingTop: theme.spacing(2),
  backgroundColor: BRAND.colors.primary,
  color: 'white',
  height: '100%',
}));

interface HeaderProps {
  showActiveIndicator?: boolean;
}

interface FlyoutColumn {
  moduleKey: 'quickSale' | 'restaurant' | 'packageManagement';
  labelKey: string;
  overviewSectionId: string;
  icon: React.ReactNode;
  color: string;
}

const Header: React.FC<HeaderProps> = ({ showActiveIndicator = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openRoleSelection } = usePublicRegistrationFlow();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [salesFlyoutOpen, setSalesFlyoutOpen] = useState(false);
  const [salesMobileOpen, setSalesMobileOpen] = useState(false);
  const salesAnchorRef = useRef<HTMLButtonElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/register/company' ||
    location.pathname === '/personnel-register';

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileMenuOpen(false);
      setSalesMobileOpen(false);
    }
  };

  const handleRegisterNavigation = () => {
    openRoleSelection();
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const isActivePath = (path: string): boolean => {
    if (path === '/features') {
      return location.pathname.startsWith('/features');
    }
    if (path === '/sales-systems') {
      return location.pathname.startsWith('/sales-systems');
    }
    if (path === '/register') {
      return (
        location.pathname === '/register' ||
        location.pathname === '/register/company' ||
        location.pathname === '/personnel-register'
      );
    }
    return location.pathname === path;
  };

  const getActiveStyle = (path: string) => {
    if (!showActiveIndicator || !isActivePath(path)) return {};
    return {
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '20%',
        width: '60%',
        height: '2px',
        backgroundColor: 'white',
      },
    };
  };

  // Sales flyout open/close (hover-friendly with delay)
  const openSalesFlyout = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setSalesFlyoutOpen(true);
  };

  const scheduleCloseSalesFlyout = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setSalesFlyoutOpen(false), 150);
  };

  const handleSalesItemClick = (sectionId: string) => {
    setSalesFlyoutOpen(false);
    if (location.pathname !== '/sales-systems') {
      navigate(`/sales-systems#${sectionId}`);
      // Navigate first; smooth scroll after render
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const flyoutColumns: FlyoutColumn[] = [
    {
      moduleKey: 'quickSale',
      labelKey: 'salesSystems.flyout.modules.quickSale.label',
      overviewSectionId: 'module-quickSale',
      icon: <BoltIcon sx={{ color: '#1565C0' }} />,
      color: '#1565C0',
    },
    {
      moduleKey: 'restaurant',
      labelKey: 'salesSystems.flyout.modules.restaurant.label',
      overviewSectionId: 'module-restaurant',
      icon: <RestaurantIcon sx={{ color: '#1E88E5' }} />,
      color: '#1E88E5',
    },
    {
      moduleKey: 'packageManagement',
      labelKey: 'salesSystems.flyout.modules.packageManagement.label',
      overviewSectionId: 'module-packageManagement',
      icon: <LocalShippingIcon sx={{ color: '#42A5F5' }} />,
      color: '#42A5F5',
    },
  ];

  // Mobile drawer
  const drawer = (
    <DrawerContent>
      <List>
        {/* Satış Sistemleri (genişletilebilir) */}
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/sales-systems')}
          onClick={() => setSalesMobileOpen((p) => !p)}
        >
          <ListItemText
            primary={t('navigation.salesSystems')}
            primaryTypographyProps={{ fontSize: '1.2rem', fontWeight: 600 }}
          />
          {salesMobileOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={salesMobileOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {flyoutColumns.map((col) => (
              <ListItem
                button
                key={col.moduleKey}
                sx={{ pl: 4, mt: 0.5 }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  setSalesMobileOpen(false);
                  handleSalesItemClick(col.overviewSectionId);
                }}
              >
                <Box sx={{ mr: 1, display: 'flex' }}>{col.icon}</Box>
                <ListItemText
                  primary={t(col.labelKey)}
                  primaryTypographyProps={{
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    color: 'white',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>

        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/pre-accounting')}
          onClick={() => handleNavigation('/pre-accounting')}
        >
          <ListItemText
            primary={t('navigation.accounting')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/ecommerce')}
          onClick={() => handleNavigation('/ecommerce')}
        >
          <ListItemText
            primary={t('navigation.ecommerce')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/qr-menu')}
          onClick={() => handleNavigation('/qr-menu')}
        >
          <ListItemText
            primary={t('navigation.qrMenu')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/kiosk')}
          onClick={() => handleNavigation('/kiosk')}
        >
          <ListItemText
            primary={t('navigation.kiosk')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/features')}
          onClick={() => handleNavigation('/features')}
        >
          <ListItemText
            primary={t('navigation.features')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/contact')}
          onClick={() => handleNavigation('/contact')}
        >
          <ListItemText
            primary={t('navigation.contact')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/terms-of-service')}
          onClick={() => handleNavigation('/terms-of-service')}
        >
          <ListItemText
            primary={t('navigation.terms')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/login')}
          onClick={() => handleNavigation('/login')}
        >
          <ListItemText
            primary={t('navigation.login')}
            primaryTypographyProps={{ fontSize: '1.2rem' }}
          />
        </ListItem>
        <ListItem
          button
          selected={showActiveIndicator && isActivePath('/register')}
          onClick={handleRegisterNavigation}
        >
          <ListItemText
            primary={t('navigation.register')}
            primaryTypographyProps={{ fontSize: '1.2rem', fontWeight: 'bold' }}
          />
        </ListItem>
      </List>
    </DrawerContent>
  );

  return (
    <>
      <StyledAppBar position="static">
        <Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Sol Bölüm - Logo */}
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {isAuthPage ? (
              <Box
                component="img"
                src={BRAND.logos.header}
                alt={`${BRAND.name} Logo`}
                sx={{
                  height: { xs: 24, sm: 26, md: 28 },
                  maxWidth: { xs: 110, sm: 120, md: 130 },
                  width: 'auto',
                  objectFit: 'contain',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': { transform: 'scale(1.03)' },
                }}
                onClick={() => navigate('/')}
              />
            ) : (
              <>
                <IconButton
                  onClick={() => navigate('/')}
                  aria-label="ana sayfa"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  <HomeIcon sx={{ fontSize: '2rem' }} />
                </IconButton>

                <Box
                  component="img"
                  src={BRAND.logos.header}
                  alt={`${BRAND.name} Logo`}
                  sx={{
                    height: { xs: 24, sm: 26, md: 30 },
                    width: 'auto',
                    objectFit: 'contain',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                  onClick={() => navigate('/')}
                />
              </>
            )}
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              {/* Orta Bölüm - Navigation Butonları */}
              <Box
                sx={{
                  flex: '1 1 0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center' }}
                >
                  {/* Satış Sistemleri (Flyout) */}
                  <Box
                    onMouseEnter={openSalesFlyout}
                    onMouseLeave={scheduleCloseSalesFlyout}
                    sx={{ display: 'inline-flex' }}
                  >
                    <NavButton
                      ref={salesAnchorRef}
                      onClick={() => navigate('/sales-systems')}
                      endIcon={
                        <KeyboardArrowDownIcon
                          sx={{
                            transition: 'transform 0.2s',
                            transform: salesFlyoutOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      }
                      sx={{
                        position: 'relative',
                        ...getActiveStyle('/sales-systems'),
                      }}
                    >
                      {t('navigation.salesSystems')}
                    </NavButton>
                  </Box>

                  <NavButton
                    onClick={() => navigate('/pre-accounting')}
                    sx={{
                      position: 'relative',
                      ...getActiveStyle('/pre-accounting'),
                    }}
                  >
                    {t('navigation.accounting')}
                  </NavButton>
                  <NavButton
                    onClick={() => navigate('/ecommerce')}
                    sx={{
                      position: 'relative',
                      ...getActiveStyle('/ecommerce'),
                    }}
                  >
                    {t('navigation.ecommerce')}
                  </NavButton>
                  <NavButton
                    onClick={() => navigate('/qr-menu')}
                    sx={{
                      position: 'relative',
                      ...getActiveStyle('/qr-menu'),
                    }}
                  >
                    {t('navigation.qrMenu')}
                  </NavButton>
                  <NavButton
                    onClick={() => navigate('/kiosk')}
                    sx={{
                      position: 'relative',
                      ...getActiveStyle('/kiosk'),
                    }}
                  >
                    {t('navigation.kiosk')}
                  </NavButton>
                  <NavButton
                    onClick={() => navigate('/features')}
                    sx={{
                      position: 'relative',
                      ...getActiveStyle('/features'),
                    }}
                  >
                    {t('navigation.features')}
                  </NavButton>
                  <NavButton
                    onClick={() => navigate('/contact')}
                    sx={{
                      position: 'relative',
                      ...getActiveStyle('/contact'),
                    }}
                  >
                    {t('navigation.contact')}
                  </NavButton>
                </Stack>
              </Box>

              {/* Sağ Bölüm - Login/Register Butonları */}
              <Box
                sx={{
                  flex: '1 1 0',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <NavButton
                  onClick={() => navigate('/login')}
                  sx={{
                    mx: 1,
                    position: 'relative',
                    ...getActiveStyle('/login'),
                  }}
                >
                  {t('navigation.login')}
                </NavButton>
                <NavButton
                  variant="outlined"
                  onClick={handleRegisterNavigation}
                  sx={{
                    position: 'relative',
                    borderColor: 'white',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                    ...getActiveStyle('/register'),
                  }}
                >
                  {t('navigation.register')}
                </NavButton>
              </Box>
            </>
          )}
        </Toolbar>
      </StyledAppBar>

      {/* Sales Flyout (Desktop) */}
      <Popper
        open={!isMobile && salesFlyoutOpen}
        anchorEl={salesAnchorRef.current}
        placement="bottom-start"
        transition
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={180}>
            <Paper
              elevation={6}
              onMouseEnter={openSalesFlyout}
              onMouseLeave={scheduleCloseSalesFlyout}
              sx={{
                mt: 0.5,
                p: 1.5,
                minWidth: { md: 270, lg: 290 },
                maxWidth: 300,
                borderRadius: 2,
                border: '1px solid rgba(13, 71, 161, 0.16)',
                backgroundColor: '#ffffff',
                boxShadow: '0 10px 24px rgba(13, 71, 161, 0.16)',
              }}
            >
              <ClickAwayListener onClickAway={() => setSalesFlyoutOpen(false)}>
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: BRAND.colors.primary }}
                    >
                      {t('salesSystems.flyout.label')}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1, opacity: 0.35 }} />
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {flyoutColumns.map((col) => (
                      <ListItem
                        key={col.moduleKey}
                        button
                        onClick={() => handleSalesItemClick(col.overviewSectionId)}
                        sx={{
                          borderRadius: 1.5,
                          backgroundColor: 'rgba(227, 242, 253, 0.26)',
                          py: 0.9,
                          px: 1,
                          transition: 'background-color 0.2s ease, transform 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(187, 222, 251, 0.34)',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        <Box sx={{ mr: 1.2, display: 'flex' }}>{col.icon}</Box>
                        <ListItemText
                          primary={t(col.labelKey)}
                          primaryTypographyProps={{
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            color: col.color,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
